import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useTranslation } from '@unifyapps/i18n/client';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { useUserStore } from '../../../../../auth/UserStoreProvider';
import type { ValueOrCondition } from '../../../../../utils/conditionalValues';
import { getConditionalPropertyValue } from '../../../../../utils/conditionalValues';
import type { OnActionArgs, OnActionType } from '../../../../context/ActionsProvider/context';
import { useGlobalAppStateStore } from '../../../../stores/app/GlobalAppStateStore';
import { InterfaceModes } from '../../../../stores/InterfaceStore';
import InterfaceHelper from '../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { useComputeConditionEvaluationGetter } from '../../../useComputeConditionEvaluationGetter';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';
import { useUpdateLocaleForPrivateInterface } from './hooks/useUpdateLocaleForPrivateInterface';
import { useUpdateLocaleForPublicInterface } from './hooks/useUpdateLocaleForPublicInterface';

export default function useSetLanguage() {
  const { t } = useTranslation(['common', 'interfaces-matrix']);
  const mode = useGlobalAppStateStore().hooks.useMode();
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const getComputedFilters = useComputeConditionEvaluationGetter();
  const userStore = useUserStore();
  const isLoggedIn = userStore.hooks.useIsLoggedIn();

  const { showSnackbar } = useSnackbar();

  const { updateLocaleForPublicInterface } = useUpdateLocaleForPublicInterface();
  const { updateLocaleForPrivateInterface } = useUpdateLocaleForPrivateInterface();

  const isPublicInterface = InterfaceHelper.isPublicInterface(interfaceRecord);

  const onSetLanguage: OnActionType = useEventCallback(async (args?: OnActionArgs) => {
    if (mode === InterfaceModes.BUILDER) {
      showSnackbar({
        color: 'warning',
        title: t('interfaces-matrix:pageActions.SetLanguage'),
      });
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    }

    const { domEvent, actionContext } = args ?? {};
    const { payload } = (args?.action ?? {}) as {
      payload: { language: ValueOrCondition<string> } | undefined;
    };

    domEvent?.stopPropagation?.();

    if (!payload?.language) {
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    }

    const computedLocale = getComputedFilters<ValueOrCondition<string>>(
      payload.language as Record<string, unknown>,
      actionContext as Record<string, unknown>,
    );

    const locale = getConditionalPropertyValue({
      value: computedLocale,
    });

    if (!locale) {
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    }

    // todo: Once backend user update call is implemented for external user, update this check to
    // 1. user is present + not external -> set in the user object
    // 2. user is present + external i.e. anonymous session -> set in the user object
    // 3. user is not present -> set in search params
    // no private/public interface checks

    // currentUserDetails is undefined in case of login flow
    if (isPublicInterface || mode === InterfaceModes.PREVIEW || !isLoggedIn) {
      await updateLocaleForPublicInterface(locale);
    } else {
      await updateLocaleForPrivateInterface(locale);
    }
    return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
  });

  return {
    onSetLanguage,
  };
}
